import defaultStore from "@/store";
import { formatBytes } from "@/js/utils.bytes";

const INGESTION_QUOTA_STATES = [
  {
    minimumRatio: 1,
    state: "full",
    level: "error",
    full: true,
    progressColor: "error",
    message: "Your data upload allowance for this period has been used."
  },
  {
    minimumRatio: 0.9,
    state: "high",
    level: "warning",
    full: false,
    progressColor: "error",
    message: "You have used 90% of your data upload allowance."
  },
  {
    minimumRatio: 0.7,
    state: "warning",
    level: "info",
    full: false,
    progressColor: "primary",
    message: "You have used 70% of your data upload allowance."
  }
];

// Keep the visual warning thresholds consistent wherever ingestion usage is
// presented. Email notifications use their own server-side delivery policy.
export const getIngestionQuotaState = quota => {
  // During the staged rollout usage is still reported, but it must not be
  // presented as a blocking allowance until the backend enforces it.
  if (!quota?.enforced) return null;
  const limit = Number(quota?.limit_bytes);
  if (!Number.isFinite(limit) || limit <= 0) return null;

  const used = Number(quota?.used_bytes || 0);
  const reserved = Number(quota?.reserved_bytes || 0);
  const committed = Number.isFinite(used) ? used : 0;
  const pending = Number.isFinite(reserved) ? reserved : 0;
  const ratio = Math.max(0, committed + pending) / limit;
  return (
    INGESTION_QUOTA_STATES.find(({ minimumRatio }) => ratio >= minimumRatio) ||
    null
  );
};

export const getIngestionQuotaAlertState = (quota, exceeded) => {
  if (exceeded) {
    const remaining = Number(exceeded.remaining_bytes || 0);
    const requested = Number(exceeded.requested_bytes || 0);
    // The backend charges uploads to the data owner. When a colleague's
    // allowance is the one exhausted, say so and offer no upgrade link:
    // upgrading the requester's own plan cannot unblock this upload.
    const ownerIsRequester = exceeded.quota_owner_is_requester !== false;
    const whose = ownerIsRequester ? "your allowance" : "the data owner's allowance";
    return {
      level: "error",
      full: true,
      message: `Upload blocked: ${whose} is exhausted (${formatBytes(remaining)} available; ${formatBytes(requested)} requested).`,
      manageDataUrl: exceeded.manage_data_url,
      upgradeUrl: ownerIsRequester ? exceeded.upgrade_url : null
    };
  }
  return getIngestionQuotaState(quota);
};

// Callers normalize their transport-specific error into this shape before
// passing it here: { status: number, payload: object }.
//
// `store` defaults to the app's singleton instead of being required: the
// http interceptor runs inside Vue's plugin setup and injects the real
// instance it was given (see addIngestionQuotaInterceptor), but the raw XHR
// upload paths in js/upload/api.js have no such injection point to thread
// through their whole call chain, so they rely on the default.
export const handleIngestionQuotaExceeded = (
  { status, payload } = {},
  store = defaultStore
) => {
  if (
    status !== 409 ||
    !payload ||
    payload.code !== "ingestion_quota_exceeded"
  ) {
    return false;
  }

  store.commit("billing/setIngestionQuotaExceeded", payload);
  return true;
};
